import { useState, useEffect } from 'react';
import type { FC, ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import GlobalSidebarNav from '../globalNav/GlobalSidebarNav';
import { useParams } from 'react-router';
import useCustomer from '../../hooks/useCustomer';
import { useNavigate } from 'react-router-dom';
import customerApi from '../../api/ActiveCustomerApi';
import NotificationsModal from '../notifications/NotificationsModal';
import NotificationsDrawer from '../notifications/NotificationsDrawer';
import ActivityForm from './overview/ActivityGrid/ActivityForm';
import GlobalHeaderNavbar from '../globalNav/GlobalHeaderNavbar';
import useCrm from 'src/hooks/useCrm';
import ConfirmationDialog from '../controls/ConfirmationDialog';
import WarningIcon from '@mui/icons-material/Warning';
import { getLocalStorageCustomerShipTo } from 'src/contexts/CustomerContext';
import { Customer } from 'src/types/customer';

interface DashboardLayoutProps {
  children?: ReactNode;
}

const DashboardLayout: FC<React.PropsWithChildren<DashboardLayoutProps>> = () => {
  let { customerNumber } = useParams();
  const [isSidebarMobileOpen, setIsSidebarMobileOpen] = useState<boolean>(false);
  const { customer, saveCustomer } = useCustomer();
  const navigate = useNavigate();
  const { activityFormState, deleteActivityOpen, setDeleteActivityOpen, activityToDelete, deleteActivity } = useCrm();

  useEffect(() => {
    if (customerNumber && customerNumber != customer.number.toString()) {
      //When the customer number changes from the url params, either by pasting a new url or changing cusomter from the customer search
      customerApi
        .getCustomer(customerNumber)
        .then((newCustomer: Customer) => {
          const shipToLocalStorage = getLocalStorageCustomerShipTo(newCustomer.numberString);

          if (shipToLocalStorage) {
            saveCustomer({
              ...newCustomer,
              selectedShipToNumber: shipToLocalStorage.shipNo,
              selectedShipToTermCode: shipToLocalStorage.termCode,
              selectedShipToWarehouse: shipToLocalStorage.warehouseCode,
            });
          } else {
            saveCustomer(newCustomer);
          }
        })
        .catch(() => {
          navigate('/404');
        });
    }
  }, [customerNumber]);

  return (
    <div className="dashboard-layout">
      <NotificationsModal />
      <NotificationsDrawer />
      {activityFormState.isFormOpen && <ActivityForm isOpen={activityFormState.isFormOpen} />}
      <ConfirmationDialog
        title={'Delete Activity?'}
        titleIconStart={<WarningIcon />}
        cancelText="No"
        okText="Yes"
        maxWidth="sm"
        open={deleteActivityOpen}
        onClose={(confirmed) => {
          setDeleteActivityOpen(false);

          if (!confirmed) {
            return;
          }

          deleteActivity();
        }}
      >
        <div>
          Are you sure you want to delete activity: <strong>{activityToDelete?.title}</strong>?
        </div>
      </ConfirmationDialog>

      <GlobalHeaderNavbar onSidebarMobileOpen={(): void => setIsSidebarMobileOpen(true)} />
      <GlobalSidebarNav onMobileClose={(): void => setIsSidebarMobileOpen(false)} openMobile={isSidebarMobileOpen} />
      <div className="dashboard-layout-container">
        <div className="dashboard-layout-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
